import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { lazy, Suspense } from 'react'
import { PublicLayout, AdminLayout } from '@/components/layout'
import { ScrollToTop, Spinner, ToastProvider } from '@/components/common'
import HeavyTabGate from '@/components/common/HeavyTabGate'

// ============================================================================
// Eager imports — critical path: pages users hit immediately after login,
// plus public marketing pages that need fast first-paint.
// ============================================================================
// Eager: Home + Login + AcceptInvite/ForgotPassword/ResetPassword (auth flows
// users hit immediately). Other public pages (About, Demo) lazy because:
//   - Demo imports GlobeContainer → pulls vendor-3d into the public landing
//   - About* are larger marketing pages, not always-needed
import Home from '@/pages/public/Home'
import Login from '@/pages/public/Login'
import AcceptInvite from '@/pages/public/AcceptInvite'
import ForgotPassword from '@/pages/public/ForgotPassword'
import ResetPassword from '@/pages/public/ResetPassword'
import Profile from '@/pages/admin/Profile'
import Users from '@/pages/admin/Users'
import UserDetail from '@/pages/admin/UserDetail'

// Eager: split About pages (Phase 5) — small enough to bundle inline,
// and the top nav points at them so first-paint matters.
import AboutDDARP from '@/pages/public/AboutDDARP'
import AboutUs from '@/pages/public/AboutUs'

// ============================================================================
// Lazy-loaded — Phase 8. These pages ship as separate chunks and only load
// when navigated to. Major bundle reductions from this:
//   - Dashboard / Nodes / Substrate import react-globe.gl + three.js (vendor-3d)
//   - Monitoring / Roadmap / Economics import recharts (vendor-viz)
//   - Wiki ships its own mermaid dependency (vendor-viz)
//   - Presentation pages are 1500+ lines each
//   - DataRoom pages bundle data-table + dialogs
//   - ComponentShowcase is dev / low-frequency
// Lazy-loading the admin pages keeps vendor-3d + vendor-viz off the public
// landing page critical path.
// ============================================================================
const Dashboard = lazy(() => import('@/pages/admin/Dashboard'))
const Nodes = lazy(() => import('@/pages/admin/Nodes'))
const Monitoring = lazy(() => import('@/pages/admin/Monitoring'))
const PulseGroups = lazy(() => import('@/pages/admin/PulseGroups'))
const TechDocs = lazy(() => import('@/pages/admin/TechDocs'))
const Wiki = lazy(() => import('@/pages/admin/Wiki'))
const Presentation = lazy(() => import('@/pages/admin/Presentation'))
const AdvantagesPresentation = lazy(() => import('@/pages/admin/AdvantagesPresentation'))
const Lifecycle = lazy(() => import('@/pages/admin/Lifecycle'))
const Audit = lazy(() => import('@/pages/admin/Audit'))
const DataRoom = lazy(() => import('@/pages/admin/DataRoom'))
const DataRoomFolder = lazy(() => import('@/pages/admin/DataRoomFolder'))
const DataRoomItem = lazy(() => import('@/pages/admin/DataRoomItem'))
const DataRoomSlide = lazy(() => import('@/pages/admin/DataRoomSlide'))
const DataRoomFavorites = lazy(() => import('@/pages/admin/DataRoomFavorites'))
const DataRoomTrashPage = lazy(() => import('@/pages/admin/DataRoomTrash'))
const DataRoomStatsPage = lazy(() => import('@/pages/admin/DataRoomStats'))
const DataRoomActivityPage = lazy(() => import('@/pages/admin/DataRoomActivity'))
const ComponentShowcase = lazy(() => import('@/pages/ComponentShowcase'))
const SharedItem = lazy(() => import('@/pages/public/SharedItem'))
const Contact = lazy(() => import('@/pages/public/Contact'))

import { useAuth } from '@/contexts/AuthContext'
import { landingFor, prefixFor, type RolePrefix } from '@/lib/rolePath'

/** Suspense fallback used by every lazy route. Centered spinner. */
const PageLoadingFallback = (
  <div className="flex items-center justify-center min-h-[60vh]">
    <Spinner size="xl" />
  </div>
)

/** Wrap a single lazy route element in Suspense — keeps spinners localized. */
function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={PageLoadingFallback}>{children}</Suspense>
}

function NotFound() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-8xl font-bold text-primary">404</h1>
        <p className="text-xl text-gray-500 mt-4">Page Not Found</p>
        <a
          href="/"
          className="inline-block mt-8 px-6 py-3 bg-primary hover:bg-primary-dark rounded-lg text-white transition-colors"
        >
          Go Home
        </a>
      </div>
    </div>
  )
}

/**
 * Gate a /admin/* | /partner/* | /portal/* subtree by the actor's role.
 *
 * - Not authenticated → /login (with `from` for return)
 * - Wrong prefix → redirect to /{myPrefix}{rest}, preserving the path
 *   so partner-hits-/admin/data-room → /partner/data-room. If that path
 *   doesn't exist in the redirected tree the user gets 404 (acceptable —
 *   sidebar never offered the link in the first place).
 */
function RolePrefixGate({
  prefix,
  children,
}: {
  prefix: RolePrefix;
  children: React.ReactNode;
}) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const myPrefix = prefixFor(user?.role);
  if (myPrefix !== prefix) {
    // Translate the URL: /admin/data-room → /partner/data-room. Falls back
    // to landing if the path is bare ("/admin").
    const rest = location.pathname.replace(/^\/(admin|partner|portal)/, '');
    const target = rest && rest !== '/' ? `/${myPrefix}${rest}` : landingFor(user?.role);
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <ToastProvider>
      <ScrollToTop />
      <AnimatePresence mode="wait">
        <Routes>
        {/* Public Routes with Layout */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          {/* Split About pages — Public Pages Refinements Phase 5 (2026-05-26).
              /about-ddarp = product/protocol; /about-us = company. */}
          <Route path="/about-ddarp" element={<AboutDDARP />} />
          <Route path="/about-us" element={<AboutUs />} />
          {/* Legacy /about → /about-ddarp (product is the natural default). */}
          <Route path="/about" element={<Navigate to="/about-ddarp" replace />} />
          {/* Legacy /about/* sub-URLs from the V1 era. /about-ddarp + /about-us
              both have client-side hash-handler useEffects that redirect
              wrong-page anchors to the correct split, so the URL ladders work. */}
          <Route path="/about/company" element={<Navigate to="/about-us#company" replace />} />
          <Route path="/about/technology" element={<Navigate to="/about-ddarp#thesis-detail" replace />} />
          <Route path="/about/project" element={<Navigate to="/about-ddarp#substrate" replace />} />
          <Route path="/contact" element={<Lazy><Contact /></Lazy>} />
          {/* Legacy /demo URL — removed in Public Pages Refinements Phase 4
              (2026-05-26). Redirect to /contact since that's where the
              former Try-the-Live-Demo CTA now points. */}
          <Route path="/demo" element={<Navigate to="/contact" replace />} />
        </Route>

        {/* Login + auth pages (no layout) */}
        <Route path="/login" element={<Login />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Presentation (standalone, no layout for fullscreen) */}
        <Route path="/presentation" element={<Lazy><Presentation /></Lazy>} />
        <Route path="/presentation/advantages" element={<Lazy><AdvantagesPresentation /></Lazy>} />

        {/* Component Showcase (for development) */}
        <Route path="/components" element={<Lazy><ComponentShowcase /></Lazy>} />

        {/* Phase 9 — public Data Room share viewer (no auth, no layout) */}
        <Route path="/share/:token" element={<Lazy><SharedItem /></Lazy>} />

        {/* =================================================================
            /admin/* — admin only. Full surface.
            ================================================================= */}
        <Route
          element={
            <RolePrefixGate prefix="admin">
              <AdminLayout />
            </RolePrefixGate>
          }
        >
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/admin/dashboard" element={<HeavyTabGate label="Dashboard"><Lazy><Dashboard /></Lazy></HeavyTabGate>} />
          <Route path="/admin/nodes" element={<HeavyTabGate label="Nodes"><Lazy><Nodes /></Lazy></HeavyTabGate>} />
          <Route path="/admin/monitoring" element={<HeavyTabGate label="Monitoring"><Lazy><Monitoring /></Lazy></HeavyTabGate>} />
          <Route path="/admin/pulse-groups" element={<HeavyTabGate label="Pulse Groups"><Lazy><PulseGroups /></Lazy></HeavyTabGate>} />
          <Route path="/admin/wiki" element={<Lazy><Wiki /></Lazy>} />
          <Route
            path="/admin/tech-docs/:sectionId?"
            element={<Lazy><TechDocs /></Lazy>}
          />
          <Route path="/admin/lifecycle" element={<Lazy><Lifecycle /></Lazy>} />
          <Route path="/admin/profile" element={<Profile />} />
          <Route path="/admin/users" element={<Users />} />
          <Route path="/admin/users/:id" element={<UserDetail />} />
          <Route path="/admin/audit" element={<Lazy><Audit /></Lazy>} />
          <Route path="/admin/data-room" element={<Lazy><DataRoom /></Lazy>} />
          <Route path="/admin/data-room/folders/:id" element={<Lazy><DataRoomFolder /></Lazy>} />
          <Route path="/admin/data-room/items/:id" element={<Lazy><DataRoomItem /></Lazy>} />
          <Route path="/admin/data-room/slides/:id" element={<Lazy><DataRoomSlide /></Lazy>} />
          <Route path="/admin/data-room/favorites" element={<Lazy><DataRoomFavorites /></Lazy>} />
          <Route path="/admin/data-room/trash" element={<Lazy><DataRoomTrashPage /></Lazy>} />
          <Route path="/admin/data-room/stats" element={<Lazy><DataRoomStatsPage /></Lazy>} />
          <Route path="/admin/data-room/activity" element={<Lazy><DataRoomActivityPage /></Lazy>} />
        </Route>

        {/* =================================================================
            /partner/* — partner only. Smaller surface (6 nav items).
            ================================================================= */}
        <Route
          element={
            <RolePrefixGate prefix="partner">
              <AdminLayout />
            </RolePrefixGate>
          }
        >
          <Route path="/partner" element={<Navigate to="/partner/dashboard" replace />} />
          <Route path="/partner/dashboard" element={<HeavyTabGate label="Dashboard"><Lazy><Dashboard /></Lazy></HeavyTabGate>} />
          <Route path="/partner/monitoring" element={<HeavyTabGate label="Monitoring"><Lazy><Monitoring /></Lazy></HeavyTabGate>} />
          <Route path="/partner/pulse-groups" element={<HeavyTabGate label="Pulse Groups"><Lazy><PulseGroups /></Lazy></HeavyTabGate>} />
          <Route path="/partner/profile" element={<Profile />} />
          <Route path="/partner/users" element={<Users />} />
          <Route path="/partner/users/:id" element={<UserDetail />} />
          <Route path="/partner/data-room" element={<Lazy><DataRoom /></Lazy>} />
          <Route path="/partner/data-room/folders/:id" element={<Lazy><DataRoomFolder /></Lazy>} />
          <Route path="/partner/data-room/items/:id" element={<Lazy><DataRoomItem /></Lazy>} />
          <Route path="/partner/data-room/slides/:id" element={<Lazy><DataRoomSlide /></Lazy>} />
          <Route path="/partner/data-room/favorites" element={<Lazy><DataRoomFavorites /></Lazy>} />
          <Route
            path="/partner/tech-docs/:sectionId?"
            element={<Lazy><TechDocs /></Lazy>}
          />
        </Route>

        {/* =================================================================
            /portal/* — user only. Smallest surface.
            ================================================================= */}
        <Route
          element={
            <RolePrefixGate prefix="portal">
              <AdminLayout />
            </RolePrefixGate>
          }
        >
          <Route path="/portal" element={<Navigate to="/portal/dashboard" replace />} />
          <Route path="/portal/dashboard" element={<HeavyTabGate label="Dashboard"><Lazy><Dashboard /></Lazy></HeavyTabGate>} />
          <Route path="/portal/nodes" element={<HeavyTabGate label="Nodes"><Lazy><Nodes /></Lazy></HeavyTabGate>} />
          <Route path="/portal/monitoring" element={<HeavyTabGate label="Monitoring"><Lazy><Monitoring /></Lazy></HeavyTabGate>} />
          <Route path="/portal/pulse-groups" element={<HeavyTabGate label="Pulse Groups"><Lazy><PulseGroups /></Lazy></HeavyTabGate>} />
          <Route path="/portal/wiki" element={<Lazy><Wiki /></Lazy>} />
          <Route
            path="/portal/tech-docs/:sectionId?"
            element={<Lazy><TechDocs /></Lazy>}
          />
          <Route path="/portal/profile" element={<Profile />} />
          <Route path="/portal/data-room" element={<Lazy><DataRoom /></Lazy>} />
          <Route path="/portal/data-room/folders/:id" element={<Lazy><DataRoomFolder /></Lazy>} />
          <Route path="/portal/data-room/items/:id" element={<Lazy><DataRoomItem /></Lazy>} />
          <Route path="/portal/data-room/slides/:id" element={<Lazy><DataRoomSlide /></Lazy>} />
          <Route path="/portal/data-room/favorites" element={<Lazy><DataRoomFavorites /></Lazy>} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
        </Routes>
      </AnimatePresence>
    </ToastProvider>
  )
}

export default App
